import { useState } from "preact/hooks";

import { fromOnboarding, saveInterestVector } from "../feed/interest";
import { markOnboarded } from "./state";
import { TOPIC_QUESTIONS } from "./topics";

export interface OnboardingProps {
  /** Called once the interest vector has been seeded and persisted. */
  onComplete: () => void;
}

/** 3–5 multi-select topic questions that seed the initial interest vector
 * (weights 1.0). Shown once, post-consent, before the personalized feed. */
export function Onboarding({ onComplete }: OnboardingProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const toggle = (tag: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const finish = async () => {
    setSaving(true);
    await saveInterestVector(fromOnboarding([...selected]));
    await markOnboarded();
    onComplete();
  };

  return (
    <div class="onboarding" role="dialog" aria-labelledby="onboarding-headline">
      <h2 id="onboarding-headline">Tune your feed</h2>
      <p class="onboarding-intro">
        Pick the topics you care about. We re-rank your feed on your device —
        these choices never leave your browser.
      </p>
      {TOPIC_QUESTIONS.map((q) => (
        <fieldset key={q.id} class="onboarding-question">
          <legend>{q.prompt}</legend>
          <div class="chips">
            {q.options.map((o) => {
              const on = selected.has(o.tag);
              return (
                <button
                  key={o.tag}
                  type="button"
                  class={on ? "chip chip-on" : "chip"}
                  aria-pressed={on}
                  onClick={() => toggle(o.tag)}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}
      <div class="onboarding-actions">
        <button class="primary" disabled={saving} onClick={() => void finish()}>
          {selected.size > 0 ? "Show my feed" : "Skip for now"}
        </button>
      </div>
    </div>
  );
}
